/**
 * Supermarkt-categorieën: shared display vocabulary for the Boodschappen
 * item tags. Values match the backend enum exactly
 * (ETM_Lists::VALID_ITEM_CATEGORIES).
 *
 * Same shape as HH-2's lib/subscriptions.js — `label` is a thunk, not a
 * string, so the Dutch text is resolved at render time rather than at
 * module load, when the i18n locale data may not be in place yet.
 *
 * The array's ORDER is the single source of truth for the supermarket walk
 * order: it drives the picker, the group headers in winkelmodus, and the
 * argument handed to groupItemsByCategory(). The backend's own constant
 * mirrors it purely so the two read the same way side by side — nothing
 * server-side depends on the order (see that constant's docblock).
 *
 * This file deliberately holds no logic. The sorting and partitioning live
 * in shoplist-sort.js, which is dependency-free and unit-tested; keeping
 * the i18n import out of there is exactly why the two are separate files.
 * Vocabulary itself is untested, same as lib/subscriptions.js.
 */
import { __ } from '../i18n.js';

export const SHOPLIST_CATEGORIES = [
  { value: 'groente_fruit', icon: 'nutrition', label: () => __('Groente & fruit', 'etm') },
  { value: 'brood', icon: 'bakery_dining', label: () => __('Brood & banket', 'etm') },
  { value: 'zuivel', icon: 'egg', label: () => __('Zuivel & eieren', 'etm') },
  { value: 'vlees_vis', icon: 'set_meal', label: () => __('Vlees & vis', 'etm') },
  { value: 'houdbaar', icon: 'inventory_2', label: () => __('Voorraadkast', 'etm') },
  { value: 'diepvries', icon: 'ac_unit', label: () => __('Diepvries', 'etm') },
  { value: 'dranken', icon: 'local_drink', label: () => __('Dranken', 'etm') },
  { value: 'verzorging', icon: 'soap', label: () => __('Verzorging', 'etm') },
  { value: 'huishouden', icon: 'cleaning_services', label: () => __('Huishoudelijk', 'etm') },
];

/** Just the stored slugs, in walk order — the argument groupItemsByCategory() expects. */
export const SHOPLIST_CATEGORY_VALUES = SHOPLIST_CATEGORIES.map((c) => c.value);

/**
 * The trailing "everything else" bucket.
 *
 * Deliberately NOT a member of SHOPLIST_CATEGORIES, and deliberately
 * different from how HH-2's subscriptions handle the same idea: there,
 * `overig` is a real stored slug the user picks. Here the ungrouped state
 * is NULL in the database and this entry exists only to give that null a
 * heading in winkelmodus. It is never written, never offered as a choice
 * (the picker's own "Geen categorie" row clears to null instead), and
 * `value: null` is what groupItemsByCategory() reports for the bucket.
 *
 * Reuses the existing "Overig" msgid rather than minting a second one.
 */
export const SHOPLIST_UNCATEGORIZED = {
  value: null,
  icon: 'category',
  label: () => __('Overig', 'etm'),
};

/**
 * Display info for a stored value. Anything unrecognised — null, undefined,
 * '', or a slug retired from the set — resolves to the uncategorised
 * bucket, which is the same catch-all rule groupItemsByCategory() applies
 * when partitioning, so a header and its rows can never disagree.
 */
export function shoplistCategoryInfo(value) {
  return SHOPLIST_CATEGORIES.find((c) => c.value === value) || SHOPLIST_UNCATEGORIZED;
}
